import { useCallback, useEffect, useReducer } from 'react';

import { BonusApi, NotificationManager, useApplicationState, useSettings } from '@atlas/core';

import { bonusStatuses } from 'common/constants';

const SET_STATE = 'SET_STATE';

const reducer = (state, { payload, type }) => {
  switch (type) {
    case SET_STATE: {
      return {
        ...state,
        ...payload,
      };
    }
    default: {
      return state;
    }
  }
};

const initialState = {
  activeBonuses: [],
  bonuses: [],
  isLoading: true,
};
const {
  ACTIVATED,
  ACTIVATION_EXPIRED,
  AVAILABLE,
  CANCELED,
  COMPLETED,
  DISABLED,
  EXPIRED,
  IN_PROGRESS,
  PENDING,
  SPEND,
} = bonusStatuses;

const statusOrder = {
  [ACTIVATED]: 3,
  [ACTIVATION_EXPIRED]: 100,
  [AVAILABLE]: 1,
  [CANCELED]: 8,
  [COMPLETED]: 5,
  [DISABLED]: 9,
  [EXPIRED]: 7,
  [IN_PROGRESS]: 2,
  [PENDING]: 4,
  [SPEND]: 6,
};

const useBonuses = () => {
  const [{ activeBonuses, bonuses, isLoading }, dispatch] = useReducer(reducer, initialState);

  const applicationState = useApplicationState();
  const { account: { currency } = {}, wallet } = applicationState;
  const { lang } = useSettings();

  const fetchBonuses = useCallback(async () => {
    dispatch({ payload: { isLoading: true }, type: SET_STATE });

    const country = applicationState.account.country || null;

    try {
      const [{ bonuses: newBonuses }, { bonuses: newActiveBonuses }] = await Promise.all([
        BonusApi.getAvailableBonuses({
          country,
          currency,
          language: lang.toUpperCase(),
        }),
        BonusApi.getActiveBonuses(lang.toUpperCase()),
      ]);

      dispatch({
        payload: {
          activeBonuses: newActiveBonuses
            ?.filter(({ status }) => status !== ACTIVATION_EXPIRED)
            ?.sort(
              ({ status: statusA }, { status: statusB }) =>
                statusOrder[statusA] - statusOrder[statusB],
            ),
          bonuses: newBonuses,
          isLoading: false,
        },
        type: SET_STATE,
      });
    } catch (error) {
      dispatch({ payload: { isLoading: false }, type: SET_STATE });
    }
  }, [applicationState.account.country, currency, lang]);

  useEffect(() => {
    if (currency) {
      fetchBonuses();
    }
  }, [currency, fetchBonuses]);

  const activateBonus = useCallback(
    async bonus => {
      const { id, wagerFreeSpinInstanceId } = bonus;

      try {
        await BonusApi.activateBonus(id, wagerFreeSpinInstanceId);
        await fetchBonuses();
      } catch (err) {
        NotificationManager.notify({
          data: err,
          message: err.code,
          status: 'error',
          type: 'notification',
        });
      }
    },
    [fetchBonuses],
  );

  const deactivateBonus = useCallback(
    async bonus => {
      const { id } = bonus;
      await BonusApi.cancelBonus(id);
      await fetchBonuses();
    },
    [fetchBonuses],
  );

  return {
    activateBonus,
    activeBonuses,
    bonuses,
    deactivateBonus,
    fetchBonuses,
    isLoading,
    wallet,
  };
};

export default useBonuses;
